"use client";

import { GraduationCap } from "lucide-react";
import { motion } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import { ParsedWebContent } from "@/lib/types/website-content";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { getFileUrl } from "@/lib/file.util";

interface HeroSectionProps {
  content: ParsedWebContent['hero'] | null;
}

export default function HeroSection({ content }: HeroSectionProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-fade functionality
  useEffect(() => {
    if (!content || content.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % content.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [content]);

  if (!content || content.length === 0) {
    // Fallback hero if no content from CMS
    return (
      <section className="relative rounded-2xl overflow-hidden mb-10 mt-4">
        <Image
          src="https://res.cloudinary.com/ddb9zukth/image/upload/v1749651796/medical-career/lzdgv5ijvurepppn6riv.jpg"
          width={1920}
          height={1080}
          alt="Medical career"
          priority
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ zIndex: 0 }}
        />
        <div className="absolute inset-0 bg-black/50" style={{ zIndex: 1 }} />
        <div className="relative z-10 flex flex-col items-center justify-center h-[340px] md:h-[60vh] px-8 md:px-16">
          <header className="py-12 md:py-16">
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center mb-6">
                <GraduationCap className="h-10 w-10 text-white mr-2" />
                <h1 className="text-3xl md:text-4xl font-bold text-white">
                  Health Careers Explorer
                </h1>
              </div>
              <p className="text-lg md:text-2xl max-w-2xl text-white mb-8">
                Discover your path to becoming a healthcare professional in Rwanda
                and make a difference in your community.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="/careers">
                  <Button className="bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-full transition-colors">
                    Explore Careers
                  </Button>
                </Link>
                <Link href="/resources">
                  <Button variant="outline" className="bg-white hover:bg-gray-100 text-green-700 font-medium py-3 px-6 rounded-full border border-green-600 transition-colors">
                    Explore Resources
                  </Button>
                </Link>
              </div>
            </div>
          </header>
        </div>
      </section>
    );
  }

  return (
    <section className="relative rounded-2xl overflow-hidden mb-10 mt-4">
      <div className="relative h-[50vh] md:h-[70vh]">
        {/* Background image with fade transition */}
        {/* <div
          className="absolute top-0 left-0 h-full bg-cover bg-center transition-all duration-1000"
          style={{
            backgroundImage: `url(${getFileUrl(content[currentSlide].image) || "https://res.cloudinary.com/ddb9zukth/image/upload/v1749651796/medical-career/lzdgv5ijvurepppn6riv.jpg"})`,
          }}
        /> */}
        <Image
          key={content[currentSlide].image}
          src={getFileUrl(content[currentSlide].image) || "https://res.cloudinary.com/ddb9zukth/image/upload/v1749651796/medical-career/lzdgv5ijvurepppn6riv.jpg"}
          alt={content[currentSlide].title}
          width={1920}
          height={1080}
          priority
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ zIndex: 0 }}
        />
        <div className="absolute inset-0 bg-black/50" style={{ zIndex: 1 }} />
        {/* Content with fade transition */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full px-8 md:px-16">
          <header className="py-12 md:py-16">
            <motion.div
              key={currentSlide}
              className="flex flex-col items-center text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center mb-6">
                <GraduationCap className="h-10 w-10 text-white mr-2" />
                <h1 className="text-3xl md:text-4xl font-bold text-white">
                  {content[currentSlide].title}
                </h1>
              </div>
              <p className="text-lg md:text-2xl max-w-2xl text-white mb-8">
                {content[currentSlide].description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {content[currentSlide].ctas?.map((cta, ctaIndex) => {
                  const isOther = cta.buttonLink === 'other';
                  const buttonLink = isOther ? cta.otherLink : cta.buttonLink;
                  return(
                  <Link key={ctaIndex} href={buttonLink}>
                    <Button
                      variant={cta.buttonVariant as any}
                      className={`font-medium py-3 px-6 rounded-full h-fit transition-colors`}
                    >
                      {cta.buttonText}
                    </Button>
                  </Link>
                )})}
              </div>
            </motion.div>
          </header>
        </div>

        {/* Dots indicator - only show if there are multiple slides */}
        {content.length > 1 && (
          <div className="absolute bottom-8  left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {content.map((_, i) => (
              <div
                key={i}
                className={`h-2 rounded-full cursor-pointer transition-all duration-300 ${
                  currentSlide === i ? "bg-white w-8" : "bg-white/50 w-2"
                }`}
                onClick={() => setCurrentSlide(i)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
